import { useEffect, useMemo, useState } from "react";

import { useFormikContext } from "formik";

import { useCurrenciesQuery } from "@sso/apis/currency";
import { FormikTextInput } from "@sso/components/Formik";
import FormikTypeahead, { AutoCompleteOption } from "@sso/components/Formik/FormikTypeahead";
import {
  Currency,
  LicenceType,
  Pricing,
  PricingFormModel,
  PricingRuleKeys,
  SoftwareLicenceFormValues,
} from "@sso/types";

import AddCurrencyApiRulesForm from "./addCurrencyApiPricingForm";
import AddCurrencyRules from "./AddCurrencyRules";

interface AddCurrencyPricingForm {
  licenceType: LicenceType;
  pricing: Pricing;
}

const AddCurrencyPricingForm = ({ licenceType, pricing }: AddCurrencyPricingForm) => {
  const { values, setFieldValue } = useFormikContext<PricingFormModel | SoftwareLicenceFormValues>();
  const { data: currencies, isLoading } = useCurrenciesQuery();
  const [currencySearchTerm, setCurrencySearchTerm] = useState<string>("");
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>();

  const existingCurrencies = useMemo(() => {
    return pricing.pricingRules
      ?.map(rule => rule.currency?.code)
      .filter((value, index, self) => value && self.indexOf(value) === index);
  }, [pricing.pricingRules]);

  const currencyOptions = useMemo(() => {
    return (
      (currencies
        ?.filter(currency => !existingCurrencies?.includes(currency.code))
        .map(currency => ({
          key: currency.code,
          value: currency.code,
          label: `${currency.symbol ?? ""} ${currency.name} (${currency.code})`,
        })) as AutoCompleteOption[]) || []
    );
  }, [currencies, existingCurrencies]);

  const onCurrencyClear = () => {
    setCurrencySearchTerm("");
  };

  const currencyChange = (currency: Currency) => {
    const newPricingRules =
      values.pricingRules?.map(rule => {
        return {
          ...rule,
          [PricingRuleKeys.CURRENCY_CODE]: currency.code,
          [PricingRuleKeys.CURRENCY]: currency,
        };
      }) || [];

    setFieldValue("pricingRules", newPricingRules);
    setSelectedCurrency(currency);
  };

  return (
    <>
      <FormikTypeahead
        disabled={isLoading}
        items={currencyOptions.filter(x => x.label.toLowerCase().includes(currencySearchTerm.toLowerCase()))}
        label="Currency"
        onSearch={(term: string) => setCurrencySearchTerm(term)}
        clear={() => onCurrencyClear()}
        name={"currencyCode"}
        handleChange={(value: string | number) => {
          currencyChange(currencies?.find(currency => currency.code === value)!);
        }}
      />
      {licenceType === LicenceType.SINGLE_USER && (
        <FormikTextInput type="number" label={"Price"} name={"pricingRules[0].price"} />
      )}
      <>
        {licenceType === LicenceType.CONCURRENT && (
          <AddCurrencyRules
            fieldAccessor="pricingRules"
            currency={selectedCurrency}
            rules={values.pricingRules ?? []}
            licenceType={licenceType}
          />
        )}
        {licenceType === LicenceType.API && values.pricingRules && (
          <AddCurrencyApiRulesForm currency={selectedCurrency} />
        )}
      </>
    </>
  );
};

export default AddCurrencyPricingForm;
